import React, { useEffect, useState } from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import { ResponsiveConfig } from "../../types/LayoutTypes";
import { ScreenData } from "../../types/ScreenTypes";
import { PrayerState } from "../../hooks/usePrayerStateManager"; // Import the PrayerState type
import VerticalLayout from "./VerticalLayout";
import LandscapeLayout from "./LandscapeLayout";
import "./shared-layout.css"; // Import shared styles first

interface ResponsiveDisplayProps {
  prayerTimes: PrayerTimes;
  tomorrowPrayerTimes?: PrayerTimes | null; // Add tomorrow's times
  screenConfig: ResponsiveConfig;
  screenData: ScreenData; // Add full screen data
  onChangesAvailable?: (available: boolean) => void;
  hasChanges?: boolean;
  prayerState: PrayerState; // Add prayerState prop
  flashingPrayer?: string | null; // Prayer key currently flashing (e.g. "JamDhuhr")
}

export default function ResponsiveDisplay({
  prayerTimes,
  tomorrowPrayerTimes = null, // Default to null
  screenConfig,
  screenData, // Include in destructuring
  onChangesAvailable,
  hasChanges,
  prayerState, // Include in destructuring
  flashingPrayer = null, // Include in destructuring
}: ResponsiveDisplayProps) {
  const [localCountdownSeconds, setLocalCountdownSeconds] = useState(
    prayerState.countdownSeconds,
  );

  // Add this outside the React.useEffect hook that updates classes
  const prevPrayerTimesRef = React.useRef(prayerTimes);

  // Apply template class to root element for theming
  React.useEffect(() => {
    const rootElement = document.documentElement;
    // Remove any existing template classes
    rootElement.classList.remove(
      "template-template1",
      "template-template2",
      "template-template3",
    );

    // Apply template class
    rootElement.classList.add(
      `template-${screenConfig.templateId || "template1"}`,
    );

    // Apply orientation class
    rootElement.classList.remove("portrait-layout", "landscape-layout");
    rootElement.classList.add(`${screenConfig.orientation}-layout`);

    // Apply responsive indicator
    rootElement.classList.add("responsive-layout");
    rootElement.classList.remove("designer-layout");
  }, [screenConfig.templateId, screenConfig.orientation]);

  // Modify the useEffect that logs prayer times
  React.useEffect(() => {
    // Only log if prayer times actually changed
    if (
      JSON.stringify(prevPrayerTimesRef.current) !== JSON.stringify(prayerTimes)
    ) {
      console.log(
        "ResponsiveDisplay received updated prayerTimes:",
        prayerTimes,
      );
      prevPrayerTimesRef.current = prayerTimes;
    }
  }, [prayerTimes]);

  useEffect(() => {
    if (!prayerState.isCountdownActive) {
      setLocalCountdownSeconds(0);
      return;
    }
    setLocalCountdownSeconds(prayerState.countdownSeconds);
    const interval = setInterval(() => {
      setLocalCountdownSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [prayerState.isCountdownActive, prayerState.countdownSeconds]);

  // Pass both today's and tomorrow's prayer times and prayerState
  return screenConfig.orientation === "portrait" ? (
    <VerticalLayout
      masjidName={screenConfig.masjidName}
      prayerTimes={prayerTimes}
      tomorrowPrayerTimes={tomorrowPrayerTimes}
      templateId={screenConfig.templateId}
      hasChanges={hasChanges}
      onChangesAvailable={onChangesAvailable}
      screenData={screenData} // Pass full screen data
      prayerState={prayerState} // Pass prayerState down
      countdownSeconds={localCountdownSeconds} // Pass countdown seconds
      flashingPrayer={flashingPrayer} // Pass flashing prayer
    />
  ) : (
    <LandscapeLayout
      masjidName={screenConfig.masjidName}
      prayerTimes={prayerTimes}
      tomorrowPrayerTimes={tomorrowPrayerTimes}
      templateId={screenConfig.templateId}
      hasChanges={hasChanges}
      onChangesAvailable={onChangesAvailable}
      screenData={screenData} // Pass full screen data
      prayerState={prayerState} // Pass prayerState down
      countdownSeconds={localCountdownSeconds} // Pass countdown seconds
      flashingPrayer={flashingPrayer} // Pass flashing prayer
    />
  );
}
